import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { usePlayer } from "@/context/PlayerContext";
import { getAchievements, trackLanguage } from "@/lib/api";
import { playWin } from "@/lib/sound";

const SEEN_KEY = "chronos.achievements.seen.v1";

function loadSeen() {
  try { return new Set(JSON.parse(localStorage.getItem(SEEN_KEY) || "[]")); }
  catch (e) { return new Set(); }
}
function saveSeen(set) {
  try { localStorage.setItem(SEEN_KEY, JSON.stringify(Array.from(set))); } catch (e) {}
}

/**
 * Silent watcher: on mount + whenever key player stats change, refetch achievements
 * and toast the ones newly unlocked (compared to localStorage-tracked "seen" set).
 * Also tracks the active i18n language on the server for the "polyglot" badge.
 */
export default function AchievementWatcher() {
  const { t, i18n } = useTranslation();
  const { player } = usePlayer();
  const runningRef = useRef(false);

  // Track language changes on the server
  useEffect(() => {
    if (!player?.id || !i18n.language) return;
    trackLanguage(player.id, i18n.language).catch(() => {});
  }, [player?.id, i18n.language]);

  // Poll achievements after progress changes
  const puzzles = player?.puzzles_solved || 0;
  const relics = (player?.relics_unlocked || []).length;
  const pages = (player?.grimoire_pages || []).length;
  const shards = player?.memory_shards || 0;

  useEffect(() => {
    if (!player?.id || runningRef.current) return;
    runningRef.current = true;
    (async () => {
      try {
        const r = await getAchievements(player.id);
        const unlocked = r.achievements.filter((a) => a.unlocked).map((a) => a.id);
        const seen = loadSeen();
        const fresh = unlocked.filter((id) => !seen.has(id));
        if (fresh.length > 0) {
          fresh.forEach((id, i) => {
            setTimeout(() => {
              const title = t(`achievements.list.${id}.title`);
              const icon = r.achievements.find((a) => a.id === id)?.icon || "🏆";
              toast.success(`${icon} ${t("achievements.unlocked_toast", { title })}`, {
                duration: 6500,
              });
              if (i === 0) playWin();
            }, i * 1200);
          });
          const next = new Set(seen);
          fresh.forEach((id) => next.add(id));
          saveSeen(next);
        }
      } catch (e) { /* ignore */ }
      finally { runningRef.current = false; }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [player?.id, puzzles, relics, pages, shards]);

  return null;
}
